<!-- Loading -->
@if (loading$ | async) {
  <div class="flex items-center justify-center py-16 text-gray-500">
    <span class="text-sm">Loading assignment...</span>
  </div>
}

<!-- Error -->
@if (error$ | async; as err) {
  <div class="max-w-2xl mx-auto px-2 sm:px-0 pt-6">
    <div
      class="rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm"
    >
      {{ err }}
    </div>
  </div>
}

<!-- Assignment content -->
@if (assignment$ | async; as assignment) {
  <div class="max-w-2xl mx-auto px-2 sm:px-0 pt-6 pb-6 space-y-4">
    <!-- Page Header -->
    <div>
      <h1 class="text-lg font-bold text-gray-900">Questions & Details</h1>
      <p class="text-sm text-gray-500 mt-1">
        Review the assignment setup, edit the title, and browse all questions
        with their correct answers.
      </p>
    </div>

    <!-- Editable Title -->
    <div class="title-card">
      <div class="flex items-center justify-between mb-1">
        <span
          class="text-xs font-medium text-gray-400 uppercase tracking-wide"
        >
          Title
        </span>
        @if (!isEditingTitle) {
          <button
            (click)="startTitleEdit(getTitle(assignment))"
            class="edit-btn"
            [style.--theme-color]="themeColor"
          >
            <lucide-icon
              [img]="icons.Pencil"
              class="w-3 h-3"
            ></lucide-icon>
            Edit
          </button>
        }
      </div>
      @if (isEditingTitle) {
        <div class="space-y-3">
          <textarea
            #titleInput
            [value]="editingTitleValue"
            (input)="onTitleInput($event)"
            (keydown.escape)="cancelTitleEdit()"
            class="w-full text-sm font-semibold text-gray-900 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:border-transparent resize-none overflow-hidden"
            [style]="'--tw-ring-color: ' + themeColor + '4D'"
            maxlength="200"
            rows="1"
          ></textarea>
          <div class="flex items-center gap-2">
            <button
              (click)="cancelTitleEdit()"
              class="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              (click)="confirmTitleEdit()"
              class="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-medium text-white transition-colors cursor-pointer"
              [style.backgroundColor]="themeColor"
            >
              <lucide-icon
                [img]="icons.Check"
                class="w-4 h-4"
              ></lucide-icon>
              Save
            </button>
          </div>
        </div>
      } @else {
        <h2 class="text-sm font-semibold text-gray-900 leading-relaxed">
          {{ getTitle(assignment) }}
        </h2>
      }
    </div>

    <!-- Assignment Info -->
    <div class="info-card">
      <app-assignment-info-card
        [assignment]="assignment"
        [tier1Name]="tier1Name()"
        [showTitle]="false"
        (testAsStudent)="onTestAsStudent(assignment)"
      />
    </div>

    <!-- Questions -->
    @if (assignment.questions.length === 0) {
      <div
        class="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center"
      >
        <p class="text-gray-500">No questions in this assignment.</p>
      </div>
    } @else {
      <div class="space-y-4">
        @for (
          question of assignment.questions;
          track question.id;
          let idx = $index
        ) {
          <div class="question-card">
            <!-- Question Header -->
            <div class="flex items-center gap-2 mb-3">
              <span
                class="question-number"
                [style.backgroundColor]="themeColor"
              >
                {{ idx + 1 }}
              </span>
              <span class="question-type-badge">
                <lucide-icon
                  [img]="getQuestionTypeIcon(question.type)"
                  class="w-3 h-3"
                ></lucide-icon>
                {{ getQuestionTypeLabel(question.type) }}
              </span>
            </div>

            <!-- Question Text -->
            <p
              class="text-sm font-medium text-gray-800 leading-relaxed mb-3"
            >
              {{ question.questionText || "Untitled question" }}
            </p>

            <!-- Multiple Choice Options -->
            @if (question.type === QuestionType.MULTIPLE_CHOICE) {
              <div class="grid grid-cols-1 gap-1.5">
                @for (
                  option of question.options;
                  track option.id;
                  let optIdx = $index
                ) {
                  <div
                    class="option-row"
                    [class.option-correct]="option.isCorrect"
                    [class.option-default]="!option.isCorrect"
                  >
                    <span
                      class="option-letter"
                      [class.option-letter-correct]="option.isCorrect"
                      [class.option-letter-default]="!option.isCorrect"
                    >
                      {{ getOptionLetter(optIdx) }}
                    </span>
                    <span
                      class="text-sm leading-snug"
                      [class.text-green-800]="option.isCorrect"
                      [class.font-medium]="option.isCorrect"
                      [class.text-gray-700]="!option.isCorrect"
                    >
                      {{ option.text }}
                    </span>
                    @if (option.isCorrect) {
                      <lucide-icon
                        [img]="icons.Check"
                        class="w-3.5 h-3.5 text-green-600 ml-auto shrink-0"
                      ></lucide-icon>
                    }
                  </div>
                }
              </div>
            }

            <!-- Short Answer / Essay placeholder -->
            @if (
              question.type === QuestionType.SHORT_ANSWER ||
              question.type === QuestionType.ESSAY
            ) {
              <div
                class="py-3 px-4 bg-gray-50 rounded-lg border border-dashed border-gray-200"
              >
                <p class="text-xs text-gray-400 italic">
                  @if (question.type === QuestionType.SHORT_ANSWER) {
                    Students will provide a short text answer
                  } @else {
                    Students will write an essay response
                  }
                </p>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
}
